import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FontSyncService } from '@ui/tablo-generator/child-name/font-sync.service';
import { TypedFormBuilder } from '@ui/form/TypedFormBuilder';
import {
  debounceTime,
  distinctUntilKeyChanged,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { TabloGroupConfig } from '@ui/tablo-generator/types';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { BehaviorSubject, combineLatest, merge, ReplaySubject } from 'rxjs';
import { UntypedFormControl } from '@angular/forms';

const randomIntFromInterval = (min, max) => {
  return Math.floor(Math.random() * (max - min + 1) + min);
};
const groupIntoRows = (v) => (previousValue, currentValue) => {
  const currentElementSize =
    currentValue.type === 'placeholder' ? currentValue.placeHolderSize : 1;
  const currentRow = previousValue[previousValue.length - 1];
  const currentRowSize = currentRow.reduce(
    (previousValue2, currentValue2) => previousValue2 + currentValue2,
    0,
  );
  if (currentRowSize + currentElementSize <= v.itemsPerRow) {
    currentRow.push(currentElementSize);
    return previousValue;
  } else {
    previousValue.push([currentElementSize]);
    return previousValue;
  }
};
const addRowNumbers = (items, v) => {
  const grouped = items.reduce(groupIntoRows(v), [[]]);
  let counter = 0;
  return grouped.flatMap((row, rowIndex) =>
    row.map((item, itemIndex) => ({
      ...items[counter++],
      row: rowIndex + 1,
    })),
  );
};
const addRandomEffects = (config: TabloGroupConfig) => ({
  nameTilt: config.childNameTilt
    ? randomIntFromInterval(-config.childNameTilt, config.childNameTilt)
    : 0,
  childTilt: config.childTilt
    ? randomIntFromInterval(-config.childTilt, config.childTilt)
    : 0,
  childPositionDistortion: config.childPositionDistortion
    ? {
        x: randomIntFromInterval(
          -config.childPositionDistortion,
          config.childPositionDistortion,
        ),
        y: randomIntFromInterval(
          -config.childPositionDistortion,
          config.childPositionDistortion,
        ),
      }
    : {
        x: 0,
        y: 0,
      },
  childOriginTransform: config.childTilt
    ? {
        x: randomIntFromInterval(0, 100),
        y: randomIntFromInterval(0, 100),
      }
    : {
        x: 0,
        y: 0,
      },
});

@Component({
    selector: 'lib-child-name-testing',
    templateUrl: './child-name-testing.component.html',
    styleUrls: ['./child-name-testing.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChildNameTestingComponent extends AliveState implements OnInit {
  @ViewChild('children', { static: true }) children: ElementRef<HTMLElement>;
  names: {
    firstName: string;
    lastName: string;
    isEmpty?: boolean;
    nameTilt?: number;
    childTilt?: number;
    childPositionDistortion?: {
      x: number;

      y: number;
    };
    childOriginTransform?: {
      x: number;

      y: number;
    };
    isPlaceholder?: boolean;

    type: 'child' | 'placeholder' | 'empty';
    index?: number;
    placeHolderSize?: number;
  }[] = [
    { firstName: 'Aga', lastName: 'Ul', type: 'child' },
    { firstName: 'Michał', lastName: 'Krupińska', type: 'child' },
    { firstName: 'Seweryn', lastName: 'Man', type: 'child' },
    { firstName: 'Patryk', lastName: 'Kluczyński-Nowacki', type: 'child' },
    { firstName: 'Ewelina', lastName: 'Barańska', type: 'child' },
    { firstName: 'Krzysztof', lastName: 'Piekorz', type: 'child' },
    { firstName: 'Michał', lastName: 'Kruk', type: 'child' },
    { firstName: 'Stanisław', lastName: 'Baran', type: 'child' },
    { firstName: 'Aga', lastName: 'Ul', type: 'child' },
    { firstName: 'Michał', lastName: 'Krupińska', type: 'child' },
    { firstName: 'Seweryn', lastName: 'Man', type: 'child' },
    { firstName: 'Patryk', lastName: 'Kluczyński-Nowacki', type: 'child' },
    { firstName: 'Ewelina', lastName: 'Barańska', type: 'child' },
    { firstName: 'Krzysztof', lastName: 'Piekorz', type: 'child' },
    { firstName: 'Michał', lastName: 'Kruk', type: 'child' },
    { firstName: 'Stanisław', lastName: 'Baran', type: 'child' },
    { firstName: 'Aga', lastName: 'Ul', type: 'child' },
    { firstName: 'Michał', lastName: 'Krupińska', type: 'child' },
    { firstName: 'Seweryn', lastName: 'Man', type: 'child' },
    { firstName: 'Patryk', lastName: 'Kluczyński-Nowacki', type: 'child' },
    { firstName: 'Ewelina', lastName: 'Barańska', type: 'child' },
    { firstName: 'Krzysztof', lastName: 'Piekorz', type: 'child' },
    { firstName: 'Michał', lastName: 'Kruk', type: 'child' },
    { firstName: 'Stanisław', lastName: 'Baran', type: 'child' },
    { firstName: 'Aga', lastName: 'Ul', type: 'child' },
    { firstName: 'Michał', lastName: 'Krupińska', type: 'child' },
    { firstName: 'Seweryn', lastName: 'Man', type: 'child' },
    { firstName: 'Patryk', lastName: 'Kluczyński-Nowacki', type: 'child' },
    { firstName: 'Ewelina', lastName: 'Barańska', type: 'child' },
    { firstName: 'Krzysztof', lastName: 'Piekorz', type: 'child' },
    { firstName: 'Michał', lastName: 'Kruk', type: 'child' },
    { firstName: 'Stanisław', lastName: 'Baran', type: 'child' },
    { firstName: 'Xawery', lastName: 'Joko', type: 'child' },
  ];
  basefontSizeFirstName = 40;
  basefontSizeLastName = 30;
  fontSizeFirstName = 40;
  fontSizeLastName = 30;
  namesListenerRegistered$ = new ReplaySubject(1);
  config = this.typedFormBuilder.typedGroup<TabloGroupConfig>({
    notAttendants: [],
    itemsPerRow: [4],
    addSpine: [false],
    spineSize: [10],
    align: ['center'],
    sideMargin: [10],
    childVerticalMargin: [10],
    childHorizontalMargin: [10],
    photoAsCircle: [false],
    photoAsSquare: [false],
    nameHandling: ['split'],
    childFirstNameHeight: [20],
    childLastNameHeight: [20],
    childNamesGap: [0],
    font: {
      fileName: null,
      fontId: null,
    },
    fontOdd: {
      fileName: null,
      fontId: null,
    },
    fontEven: {
      fileName: null,
      fontId: null,
    },
    fontLatterSpacing: [1],
    fontLatterSpacingOdd: [1],
    fontLatterSpacingEven: [1],
    photoAsBackground: [false],
    childPhotoOverlayFileName: [null],
    childPhotoOverlayFileNameEven: [null],
    childPhotoOverlayFileNameOdd: [null],
    childPhotoOverlayHasColorOverlay: [false],
    childPhotoOverlayHasColorOverlayEven: [false],
    childPhotoOverlayHasColorOverlayOdd: [false],
    childPhotoOverlayColorOverlay: [''],
    childPhotoOverlayColorOverlayEven: [''],
    childPhotoOverlayColorOverlayOdd: [''],
    childPhotoOverlayScale: [1],
    childPhotoOverlayScaleEven: [1],
    childPhotoOverlayScaleOdd: [1],
    childConfigDifferenceRule: ['none'],
    childFrameWidth: [0],
    childFrameWidthOdd: [0],
    childFrameWidthEven: [0],
    childFrameColor: [''],
    childFrameColorOdd: [''],
    childFrameColorEven: [''],
    photoSideMargins: [0],
    photoSideMarginsOdd: [0],
    photoSideMarginsEven: [0],
    childPhotoFrameWidth: [0],
    childPhotoFrameWidthOdd: [0],
    childPhotoFrameWidthEven: [0],
    childPhotoFrameColor: [''],
    childPhotoFrameColorOdd: [''],
    childPhotoFrameColorEven: [''],
    childLastNameMaxFontSize: [30],
    childFirstNameMaxFontSize: [40],
    childNameBackgroundColor: [null],
    childNameBackgroundColorEven: [null],
    childNameFrameWidth: [0],
    childNameBackgroundColorOdd: [null],
    childPhotoShadow: [null],
    childPhotoShadowEven: [null],
    childPhotoShadowOdd: [null],
    childNameFrameWidthEven: [0],
    childNameFrameWidthOdd: [0],
    childNameNameFrameColor: [null],
    childNameNameFrameColorEven: [null],
    childNameNameFrameColorOdd: [null],
    childPhotoNameToPhotoGap: [0],
    childPhotoNameFontColor: [''],
    childPhotoNameFontColorEven: [''],
    childPhotoNameFontColorOdd: [''],
    childNameSideMargins: [0],
    childNameSideMarginsEven: [0],
    childNameSideMarginsOdd: [0],

    childNameFrameRadiusEven: [0],
    childNameFrameRadiusOdd: [0],
    childNameFrameRadius: [0],
    childBackgroundColor: [null],
    childBackgroundColorEven: [null],
    childBackgroundColorOdd: [null],
    childNameTilt: [0],
    childPositionDistortion: [0],
    childTilt: [0],
    photoRadius: [0],
    photoRadiusEven: [0],
    photoRadiusOdd: [0],
    childShadow: [null],
    childShadowEven: [null],
    childShadowOdd: [null],
    childRadius: [0],
    childRadiusEven: [0],
    childRadiusOdd: [0],
  });
  placeholders = new BehaviorSubject<{ index: number; size: number }[]>([]);
  childrenCount = new UntypedFormControl(14);
  namesList$ = this.childrenCount.valueChanges.pipe(
    tap(() => {
      this.placeholders.next([]);
    }),
    startWith(this.childrenCount.value),
    map((v) => this.names.slice(0, v)),
    shareReplay(1),
  );
  names$ = combineLatest([
    this.config.valueChanges.pipe(startWith(this.config.value)),
    this.childrenCount.valueChanges.pipe(startWith(this.childrenCount.value)),
    this.namesList$,

    this.placeholders,
  ]).pipe(
    map(([v, childLimit, namesList, placeholders]) => {
      const countElementsInPreviousRows = (
        rowIndex: number,
        currentNames: { type?: any; size? }[],
        itemsPerRow: number,
        namesGroupedByRows,
      ) => {
        const previousRows = namesGroupedByRows.filter((_, i) => i < rowIndex);
        return (
          previousRows.reduce(
            (previousValue, currentValue) =>
              previousValue + currentValue.length,
            0,
          ) +
          namesGroupedByRows[rowIndex].reduce(
            (previousValue, currentValue, currentIndex) => {
              if (
                previousValue + currentValue <=
                namesGroupedByRows[rowIndex].length / 2
              ) {
                return previousValue + 1;
              }
              return previousValue;
            },
            0,
          )
        );
      };
      if (v.addSpine && v.itemsPerRow % 2 === 0) {
        const current = [
          ...[...namesList].slice(0, childLimit),
          ...placeholders.map(({ index, size }) => ({
            isEmpty: true,
            index,
            placeHolderSize: size,
            isPlaceholder: true,
            childPositionDistortion: { x: 0, y: 0 },
            childOriginTransform: { x: 0, y: 0 },
            type: 'placeholder',
          })),
        ];
        switch (v.align) {
          case 'left':
            Array(current.length % v.itemsPerRow)
              .fill(0)
              .forEach((v) => {
                current.push({
                  isEmpty: true,
                  firstName: '',
                  lastName: '',
                  type: 'empty',
                });
              });
            break;
          case 'right':
            Array(v.itemsPerRow - (current.length % v.itemsPerRow))
              .fill(0)
              .forEach((item) => {
                const start =
                  v.itemsPerRow * Math.floor(current.length / v.itemsPerRow);
                current.splice(start, 0, {
                  isEmpty: true,
                  firstName: '',
                  lastName: '',
                  type: 'empty',
                });
              });
            break;

          case 'center':
            if (current.length % 2 !== 0) {
              current.push({
                isEmpty: true,
                firstName: '',
                lastName: '',
                type: 'empty',
              });
            }
            break;
        }
        const currentWithIndexAndSorted = current
          .map((_, i) => ({
            ..._,
            index: _.type !== 'placeholder' ? i + 1 : _.index,
          }))
          .sort((a, b) => a.index - b.index);
        const namesGroupedByRows = currentWithIndexAndSorted.reduce(
          groupIntoRows(v),
          [[]],
        );

        return [
          ...addRowNumbers(currentWithIndexAndSorted, v),
          ...Array(Math.ceil(current.length / v.itemsPerRow))
            .fill(0)
            .flatMap((_, i) => {
              const index = countElementsInPreviousRows(
                i,
                currentWithIndexAndSorted,
                v.itemsPerRow,
                namesGroupedByRows,
              );
              return [
                {
                  index: currentWithIndexAndSorted[index - 1].index + 0.1,
                  isSpine: true,
                  type: 'empty',
                },
                {
                  index: currentWithIndexAndSorted[index - 1].index + 0.1,
                  isSpine: true,
                  type: 'empty',
                },
              ];
            }),
        ]
          .sort((a, b) => a.index - b.index)
          .map((value) => ({
            ...value,
            ...addRandomEffects(v),
          }));
      }
      // namesList.splice(5, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });
      // namesList.splice(6, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });
      // namesList.splice(9, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });
      // namesList.splice(10, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });

      return addRowNumbers(
        [
          ...namesList.slice(0, childLimit),
          ...placeholders.map(({ index, size }) => ({
            isEmpty: true,
            index,
            placeHolderSize: size,
            isPlaceholder: true,
            childPositionDistortion: { x: 0, y: 0 },
            childOriginTransform: { x: 0, y: 0 },
            type: 'placeholder',
          })),
        ]
          .map((_, i) => ({
            ..._,
            ...addRandomEffects(v),
            index: _.type === 'placeholder' ? _.index : i + 1,
          }))
          .sort((a, b) => a.index - b.index),
        v,
      );
    }),
  );
  itemWidth$ = this.config.valueChanges.pipe(
    startWith(this.config.value),
    map((v) => {
      if (v.addSpine) {
        return `calc((100% - ${v.spineSize * 2}px) / ${v.itemsPerRow})`;
      } else {
        return `${100 / v.itemsPerRow}%`;
      }
    }),

    shareReplay(1),
  );

  itemHeight$ = combineLatest([
    this.config.valueChanges.pipe(startWith(this.config.value)),
    this.names$,
  ]).pipe(
    map(([v, namesList]) => 100 / namesList[namesList.length - 1].row),
    shareReplay(1),
  );

  constructor(
    private fontSyncService: FontSyncService,
    private typedFormBuilder: TypedFormBuilder,
  ) {
    super();
  }

  addPlaceHolder(index, size) {
    this.placeholders.next([
      ...this.placeholders.value.filter((v) => v.index < index),
      {
        index:
          index -
          1 +
          0.1 *
            (1 +
              this.placeholders.value.filter(
                (v) => v.index < index && v.index > index - 1,
              ).length),
        size,
      },
    ]);
  }
  onDrag({ target, beforeTranslate }) {
    console.log(beforeTranslate);
    // this.frame.translate = beforeTranslate;
    target.style.transform = `translate(${beforeTranslate[0]}px, ${beforeTranslate[1]}px)`;
  }
  onResize({ target, width, height, drag }) {
    target.style.width = `${width}px`;
    target.style.height = `${height}px`;

    // get drag event
    target.style.transform = `translate(${drag.beforeTranslate[0]}px, ${drag.beforeTranslate[1]}px)`;
  }
  ngOnInit(): void {
    const nameHandlingChanged = merge(
      this.config.valueChanges.pipe(distinctUntilKeyChanged('nameHandling')),
      this.config.valueChanges.pipe(
        distinctUntilKeyChanged('childLastNameMaxFontSize'),
      ),
      this.config.valueChanges.pipe(
        distinctUntilKeyChanged('childFirstNameMaxFontSize'),
      ),

      this.config.valueChanges.pipe(
        distinctUntilKeyChanged('childFirstNameHeight'),
      ),
      this.config.valueChanges.pipe(
        distinctUntilKeyChanged('childLastNameHeight'),
      ),
      this.config.valueChanges.pipe(distinctUntilKeyChanged('childNamesGap')),
      this.config.valueChanges.pipe(
        distinctUntilKeyChanged('childNameFrameWidth'),
      ),
      this.config.valueChanges.pipe(
        distinctUntilKeyChanged('childNameSideMargins'),
      ),
    ).pipe(startWith(this.config.value), debounceTime(500));
    this.subscribeWhileAlive(
      combineLatest([
        nameHandlingChanged.pipe(
          tap((x) => console.log('name handling changes')),
        ),
        this.names$.pipe(map((v) => v.filter((v) => v.type === 'child'))),
      ]).pipe(
        switchMap(([v, namesList]) => {
          this.fontSizeFirstName = v.childFirstNameMaxFontSize;
          this.fontSizeLastName = v.childLastNameMaxFontSize;
          switch (v.nameHandling) {
            case 'split':
              this.fontSyncService
                .registerContext('name', namesList.length)

                .pipe(takeUntil(nameHandlingChanged))

                .subscribe((v) => {
                  this.fontSizeLastName = v;
                });
              console.log('listen to fonts');
              this.fontSyncService
                .registerContext('firstName', namesList.length)
                .pipe(takeUntil(nameHandlingChanged))

                .subscribe(
                  (v) => {
                    this.fontSizeFirstName = v;
                  },
                  () => null,
                  () => console.log('completed'),
                );
              this.namesListenerRegistered$.next(true);
              return this.fontSyncService.everythingCompleted().pipe(
                tap((v) => {
                  console.log('ALL COMPLETED');
                }),
              );
            case 'join':
              this.fontSyncService
                .registerContext('name', namesList.length)
                .pipe(takeUntil(nameHandlingChanged))
                .subscribe((v) => {
                  this.fontSizeFirstName = v;
                });
              this.namesListenerRegistered$.next(true);
              return this.fontSyncService.everythingCompleted().pipe(
                tap((v) => {
                  console.log('ALL COMPLETED');
                }),
              );
          }
        }),
      ),
    );
  }

  removePlaceholder(index) {
    this.placeholders.next(
      this.placeholders.value.filter((v) => v.index !== index),
    );
  }
}
